import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  getCustomerByIdService,
  createCustomerService,
  updateCustomerService,
} from "../../services/customers/services";
import { useNotifications } from "../../context/Notifications";
import { useTranslator } from "../../context/translator";
import HistoryModal from "../historyModal/HistoryModal";
import ClientFormBasic from "../clientFormBasic/ClientFormBasic";
import ClientFormCategory from "../clientFormCategory/ClientFormCategory";
import ClientFormContact from "../clientFormContact/ClientFormContact";
import ClientFormShops from "../clientFormShops/ClientFormShops";
import ClientFormAddress from "../clientFormAddress/ClientFormAddress";
import ClientFormActions from "../clientFormActions/ClientFormActions";
import ClientFormIP from "../clientFormIP/ClientFormIP";
import ClientFormDateSignUp from "../clientFormDateSignUp/ClientFormDateSignUp";
import ClientRelatedTo from "../clientRelatedTo/ClientRelatedTo";
import { PERMISSION_CODE } from "../../utils/enum/enums";
import { ValidatePermission } from "../../utils/allowHelper/AllowHelper";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const ClientEdit = ({ clientId }) => {
  const navigate = useNavigate();
  const { translate } = useTranslator();
  const { addNotification } = useNotifications();
  const { user } = useSelector((state) => state.user);
  const [companyAllow, setCompanyAllow] = useState([]);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({});

  const [client, setClient] = useState({
    firstName: "",
    lastName: "",
    shopName: "",
    contactName: "",
    accountNumber: "",
    email: "",
    phoneNumber: "",
    extPhoneNumber: "-",
    shops: 0,
    expState: "cmp",
    ipAddress: "",
    dateSignUp: "",
    verificationName: false,
    verificationEmail: false,
    verificationPhoneNumber: false,
    verificationAddress: false,
    verificationIP: false,
    status: "active",
    categoryId: "0",
    country: { id: 0, name: "No Selected" },
    state: { id: 0, name: "No Selected" },
    city: { id: 0, name: "No Selected" },
    streetAddress: "",
    zipCode: "",
    relatedTo: null,
  });

  const getClient = useCallback(async () => {
    if (clientId && clientId !== "new") {
      const data = await getCustomerByIdService({ customerId: clientId });
      if (data?.customer) setClient(data.customer);
    }
  }, [clientId]);

  useEffect(() => {
    getClient();
  }, [getClient]);

  useEffect(() => {
    if (company && company?.allow) {
      setCompanyAllow({ ...company?.allow });
    }
  }, [company]);

  const handleCancel = () => navigate(-1);

  const handleOnChange = (e) => {
    const { name, value, checked } = e.target;
    const val = e.target.type === "checkbox" ? checked : value;
    setClient((prev) => ({ ...prev, [name]: val }));
  };
  const handleRelatedTo = (value) => {
    setClient((prev) => ({ ...prev, relatedTo: value }));
  };

  const handleOnCategory = (value) => {
    setClient((prev) => ({ ...prev, categoryId: value }));
  };

  const handleOnChangeAddress = (name, value) => {
    if (name === "country") {
      setClient((prev) => ({
        ...prev,
        country: value,
        state: { id: 0, name: "No Selected" },
        city: { id: 0, name: "No Selected" },
      }));
      return;
    } else if (name === "state") {
      setClient((prev) => ({
        ...prev,
        state: value,
        city: { id: 0, name: "No Selected" },
      }));
      return;
    } else if (name === "city") {
      setClient((prev) => ({
        ...prev,
        city: value,
      }));
      return;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const request =
      clientId !== "new"
        ? updateCustomerService({ customerId: clientId, data: client })
        : createCustomerService({
            data: {
              ...client,
              companyId: company?._id,
            },
          });

    const response = await request;
    const success =
      response?.customer?.acknowledged ||
      response?.customer?.customers?.acknowledged;
    const customerId =
      response?.customer?.insertedId ||
      response?.customer?.customers?.insertedId;

    if (response?.customer?.missingFields) {
      setForm(response?.customer?.missingFields);
    }

    addNotification({
      id: Date.now(),
      message: success
        ? clientId !== "new"
          ? translate("customer-updated")
          : translate("customer-created")
        : clientId !== "new"
          ? translate("error-updating-customer")
          : translate("error-creating-customer"),
      kind: success ? "success" : "danger",
    });

    if (clientId === "new" && success && customerId)
      navigate(`/clients/${customerId}`);

    //if (success) navigate(-1);
    setIsLoading(false);
  };

  const readOnly = React.useCallback(() => {
    const hasPermissions =
      ValidatePermission(userProfile?.user, [PERMISSION_CODE.EDIT_CUSTOMER]) ||
      (ValidatePermission(userProfile?.user, [
        PERMISSION_CODE.CREATE_CUSTOMER,
      ]) &&
        clientId === "new");
    return !hasPermissions;
  }, [clientId, userProfile?.user]);

  if (userLoading || companyLoading) return null;

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid gap-6 mb-6 md:grid-cols-2">
        <ClientFormBasic
          client={client}
          onChange={handleOnChange}
          allow={companyAllow}
          translate={translate}
          clientId={clientId}
          form={form}
          readOnly={readOnly()}
        />
        <ClientFormCategory
          client={client}
          onChange={handleOnCategory}
          translate={translate}
          readOnly={readOnly()}
        />
        <ClientRelatedTo
          key={"related-to"}
          allow={companyAllow}
          relatedTo={client.relatedTo}
          onChange={handleRelatedTo}
          label={translate("related-to")}
          isMulti={true}
          readOnly={readOnly()}
          customerId={client?._id}
        />
        <ClientFormDateSignUp
          client={client}
          onChange={handleOnChange}
          translate={translate}
          allow={companyAllow}
          form={form}
          readOnly={readOnly()}
        />
        <ClientFormContact
          client={client}
          onChange={handleOnChange}
          allow={companyAllow}
          translate={translate}
          form={form}
          readOnly={readOnly()}
        />
        <ClientFormShops
          client={client}
          onChange={handleOnChange}
          allow={companyAllow}
          translate={translate}
          form={form}
          readOnly={readOnly()}
        />
        <ClientFormIP
          client={client}
          onChange={handleOnChange}
          allow={companyAllow}
          translate={translate}
          form={form}
          readOnly={readOnly()}
        />
        <ClientFormAddress
          client={client}
          onChange={handleOnChangeAddress}
          onChangeCheckbox={handleOnChange}
          allow={companyAllow}
          translate={translate}
          form={form}
          readOnly={readOnly()}
        />
      </div>
      {clientId !== "new" && !readOnly() && (
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            {translate("comm")}
          </label>
          <HistoryModal customer={client} key="logsCustomer" />
        </div>
      )}
      <ClientFormActions
        onCancel={handleCancel}
        isLoading={isLoading}
        translate={translate}
        readOnly={readOnly()}
      />
    </form>
  );
};

export default ClientEdit;
